/**
 * CSV user upload — parsing, preflight validation, and payload assembly.
 *
 * Deliberately pure and React-free: every rule here is a data-in/data-out
 * function, so the validation can be exercised directly rather than only through
 * a dialog. `UserUploadDialog.jsx` is the thin UI over this.
 *
 * The file shape (header row required, one user per row):
 *
 *   first | last | username | email | role | password
 *
 * `first` is read but **not used** — `last` carries the whole display name in this
 * format (the sample file has first="Jasmine", last="Jasmine Del Blanco"). The
 * column is tolerated so the sheet people already have keeps working; it is not
 * required and never mapped.
 *
 * Column → add-user payload:
 *
 *   last     → display_name
 *   username → username
 *   email    → email
 *   role     → user_role_id   (matched by the role NAME shown in the drawer's dropdown)
 *   password → password
 */

/* The roles accepted in the `role` column, each with every spelling that means it.
 *
 * A name on its own is not enough: the payload needs a `user_role_id`, which only
 * the campus's live role list has. So a CSV value and a `user_role_name` both have
 * to resolve to the same thing, and THREE different vocabularies are in play:
 *
 *   what people type       admin
 *   att_user_roles         Administrator        (user_role_id 5)
 *   backend slug map       admin                (Portal_User_Manager.swapRole)
 *
 * `normalizeRoleName` folds case, spaces, hyphens and underscores, which covers
 * `super-admin` vs `Super Admin`. It cannot cover `admin` vs `Administrator` —
 * those are different words. Hence `aliases`: both spellings are declared as the
 * same approved role, and `approvedRoleLabel` maps either side onto the shared
 * label. Matching raw names is what made a file saying `admin` fail on a campus
 * that has exactly that role, and hid it from the "roles you can use" list too.
 *
 * Adding a role here does NOT grant anyone the right to create it — the per-row
 * authority check against the uploader's weight still applies. */
export const APPROVED_ROLES = [
  { label: 'caregiver', aliases: ['caregiver'] },
  /* `att_user_roles` calls role id 5 "Administrator" while everyone types "admin",
     and no amount of case/space folding turns one into the other. Both spellings
     belong to the same approved role, so both are listed here. This is the whole
     reason aliases exist rather than a flat list of names. */
  { label: 'admin', aliases: ['admin', 'administrator'] },
  { label: 'super admin', aliases: ['super admin'] },
  { label: 'corporate', aliases: ['corporate'] },
  { label: 'corporate admin', aliases: ['corporate admin'] },
  { label: 'vendor', aliases: ['vendor'] },
];

/* The primary spellings, for the "must be one of" message. */
export const APPROVED_ROLE_NAMES = APPROVED_ROLES.map((r) => r.label);

/* Every accepted spelling → its approved role's label. Both a CSV value and a
   live `user_role_name` are looked up through this, which is what lets a file
   saying `admin` resolve a role named `Administrator`: the two land on the same
   label, so they are the same role. */
const ALIAS_TO_LABEL = new Map();
APPROVED_ROLES.forEach(({ label, aliases }) => {
  aliases.forEach((a) => ALIAS_TO_LABEL.set(a, label));
});

/**
 * One spelling for role comparison: lower-cased, punctuation folded to spaces,
 * runs of whitespace collapsed.
 *
 * `Super Admin`, `super-admin`, `SUPER_ADMIN` and `  super  admin ` all normalize
 * to `super admin`.
 */
export const normalizeRoleName = (name) =>
  String(name ?? '')
    .toLowerCase()
    .replace(/[-_/]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/* The one role that does not need an email address. Same rule the Add User drawer
   already applies (UserDrawer.jsx `isCaregiver`), so a row that passes here is a
   row the drawer would also have accepted. */
export const EMAIL_EXEMPT_ROLE = 'caregiver';

/* Password rules, matching UserDrawer / ResetPasswordDialog exactly: six
   characters minimum AND alphanumeric only.

   The alphanumeric half is not an extra invented here — it is the rule the rest of
   the app enforces, and a preflight that passed `pass-word` would hand the user a
   green tick on a row the backend then refuses. If the platform rule ever relaxes,
   these two constants and the three hand-copied `isPasswordFormatValid` functions
   are the places to change. */
export const PASSWORD_MIN_LENGTH = 6;
export const PASSWORD_PATTERN = /^[A-Za-z0-9]+$/;

/* One more rule the Add User drawer enforces and a length check alone would miss:
   role ids 1 and 2 (the admin roles) may not be created with this password. Same
   ids and same literal as UserDrawer's `isAdminWeakPassword`. */
export const ADMIN_ROLE_IDS = [1, 2];
export const WEAK_ADMIN_PASSWORD = '123456';

/* Deliberately permissive: one @, something before it, a dotted domain after, no
   whitespace. Full RFC 5322 in a regex rejects addresses that work and accepts
   ones that do not; the only useful job here is catching a typo or a name that
   ended up in the wrong column. */
export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/* Columns the file must actually provide. `first` and `email` are absent on
   purpose: `first` is never mapped, and `email` is required per-row rather than
   per-file (see requiresEmail). */
export const REQUIRED_COLUMNS = ['last', 'username', 'role', 'password'];

/**
 * Rows per request.
 *
 * **10**, chosen from measurement rather than taste: 30 rows took 15-20s end to
 * end, so ~0.5-0.7s per row, and ten rows puts a progress update on screen every
 * 5-7s. Feedback cadence is the only thing this trades — the endpoint accepts any
 * size, since `deleteUsers` already posts N items in a single Payload array.
 *
 * Two ways to move it, both one edit here:
 *   - a positive number  → sequential requests of that size, progress per request
 *   - `0`                → one request containing every row
 *
 * `chunkPayloads` handles both and `uploadInChunks` walks whatever it returns, so
 * nothing else changes. Bear in mind a larger size means a coarser progress
 * readout: with a single request there is no intermediate progress at all, and the
 * dialog switches to "Uploading N users…" rather than showing a counter that
 * cannot move. Failure granularity follows the same line — a failed request stops
 * the upload, and everything in earlier requests has already been created.
 */
export const UPLOAD_CHUNK_SIZE = 1;

/** Trim, and treat null/undefined as ''. Excel leaves plenty of both. */
const clean = (value) => (value == null ? '' : String(value).trim());

/** Header keys arrive with stray case, spaces, and sometimes a BOM. */
export const normalizeHeader = (header) =>
  clean(header).toLowerCase().replace(/^﻿/, '');

/**
 * The approved role label a spelling belongs to, or '' when it is not approved.
 *
 * Used for BOTH sides of the comparison — the CSV's `role` value and the campus's
 * `user_role_name` — so "admin" and "Administrator" resolve to the same label and
 * therefore the same role.
 */
export const approvedRoleLabel = (name) => ALIAS_TO_LABEL.get(normalizeRoleName(name)) || '';

/** Does a row with this role need an email address? */
export const requiresEmail = (roleName) =>
  approvedRoleLabel(roleName) !== EMAIL_EXEMPT_ROLE
  && normalizeRoleName(roleName) !== EMAIL_EXEMPT_ROLE;

/**
 * Is a parsed row completely empty?
 *
 * Excel writes trailing comma-only lines for every row the user ever touched, and
 * the sample sheet has a dozen of them. Without this each one becomes four
 * "missing required field" errors and the real problems are buried.
 */
export const isBlankRow = (row) =>
  !row || Object.values(row).every((v) => clean(v) === '');

/**
 * Validate parsed CSV rows against the campus's roles and the uploader's authority.
 *
 * Collects EVERY problem rather than stopping at the first. The master-file
 * precheck on CampusProfilePage breaks on the first error and toasts it, which
 * means fixing a ten-row file is ten upload attempts; a list of all of them is one.
 *
 * @param {Array<object>} rows — PapaParse `data` with header:true
 * @param {object} options
 * @param {Array<{user_role_id: number, user_role_name: string, user_role_weight: number}>} options.roles
 * @param {number} options.myRoleWeight — the uploader's weight
 * @param {(compared: number, mine: number) => boolean} options.canAssignWeight —
 *        pass `iAmHigherAuthority`; injected rather than imported so this module
 *        stays free of app singletons
 * @param {Array<string>} [options.existingUsernames]
 * @param {Array<string>} [options.headers] — parsed header names, for the shape check
 * @returns {{ok: boolean, totalRows: number, issues: Array<{row: number|null, field: string, message: string}>, payloads: Array<object>}}
 */
export function validateUserCsv(rows, {
  roles = [],
  myRoleWeight,
  canAssignWeight,
  existingUsernames = [],
  headers = null,
} = {}) {
  const issues = [];
  const payloads = [];

  const addIssue = (row, field, message) => issues.push({ row, field, message });

  /* Live roles keyed by their APPROVED LABEL rather than by their own name. That
     indirection is the fix for `admin` vs `Administrator`: the campus's role name
     and the spelling a person types are both resolved to a label first, so they
     meet in the middle instead of having to be spelled identically.

     Built once — a 500-row file would otherwise scan the role list 500 times. */
  const rolesByLabel = new Map();
  (roles || []).forEach((r) => {
    const label = approvedRoleLabel(r?.user_role_name);
    if (label) rolesByLabel.set(label, r);
  });

  /* What this uploader could actually put in the role column, for the error
     message. Derived from the campus's live roles rather than from
     APPROVED_ROLE_NAMES: telling someone "admin is not available" while the
     approved list says `admin` is accepted is the confusing half of the original
     bug. Naming the real, assignable options makes a mismatch self-diagnosing. */
  const assignableNames = (roles || [])
    .filter((r) => approvedRoleLabel(r?.user_role_name))
    .filter((r) => typeof canAssignWeight !== 'function'
      || canAssignWeight(r.user_role_weight, myRoleWeight))
    .map((r) => r.user_role_name)
    .filter(Boolean);

  const takenUsernames = new Set(
    (existingUsernames || []).map((u) => clean(u).toLowerCase()).filter(Boolean),
  );

  /* Header check first: a file with the wrong columns produces one clear message
     instead of an identical error on every row. */
  if (Array.isArray(headers)) {
    const present = new Set(headers.map(normalizeHeader));
    REQUIRED_COLUMNS.forEach((col) => {
      if (!present.has(col)) {
        addIssue(null, col, `The file has no "${col}" column.`);
      }
    });
    if (issues.length) return { ok: false, totalRows: 0, issues, payloads: [] };
  }

  const allRows = rows || [];
  const dataCount = allRows.filter((r) => !isBlankRow(r)).length;
  if (dataCount === 0) {
    addIssue(null, 'file', 'The file has no user rows.');
    return { ok: false, totalRows: 0, issues, payloads: [] };
  }

  /* Usernames claimed earlier in this same file. Two rows with one username would
     create the first and fail the second, leaving a half-applied upload — the
     exact thing a preflight exists to prevent. */
  const seenInFile = new Map();

  /* Blank rows are skipped INSIDE this loop rather than filtered beforehand, and
     that is what keeps the row numbers honest.

     Row numbers are the spreadsheet's, counting the header as row 1, so a message
     points at the line the user is looking at. That only holds if the index here
     still matches the file: filtering blanks out first — or letting PapaParse's
     skipEmptyLines do it — shifts every subsequent index down by the number of
     blanks above it, so one empty line in the middle of a sheet silently makes
     every error below it point one row too high. The caller therefore parses with
     skipEmptyLines OFF and this owns the skipping. */
  allRows.forEach((row, index) => {
    if (isBlankRow(row)) return;
    const rowNum = index + 2;

    const displayName = clean(row.last);
    const username = clean(row.username);
    const email = clean(row.email);
    const roleName = clean(row.role);
    const password = clean(row.password);

    if (!displayName) addIssue(rowNum, 'last', 'Missing last (display name).');

    if (!username) {
      addIssue(rowNum, 'username', 'Missing username.');
    } else {
      const key = username.toLowerCase();
      if (takenUsernames.has(key)) {
        addIssue(rowNum, 'username', `Username "${username}" already exists at this campus.`);
      }
      if (seenInFile.has(key)) {
        addIssue(rowNum, 'username', `Username "${username}" is also used on row ${seenInFile.get(key)}.`);
      } else {
        seenInFile.set(key, rowNum);
      }
    }

    // Role: approved name, known to this campus, and one the uploader may assign.
    let role = null;
    const roleLabel = approvedRoleLabel(roleName);
    if (!roleName) {
      addIssue(rowNum, 'role', 'Missing role.');
    } else if (!roleLabel) {
      addIssue(rowNum, 'role', `Role "${roleName}" is not one of: ${APPROVED_ROLE_NAMES.join(', ')}.`);
    } else {
      role = rolesByLabel.get(roleLabel) || null;
      if (!role) {
        /* An approved spelling that this campus's role list does not contain under
           any equivalent name. Says what IS available so the fix is obvious. */
        const options = assignableNames.length
          ? ` Roles you can use here: ${assignableNames.join(', ')}.`
          : ' No roles are available for you to assign at this campus.';
        addIssue(rowNum, 'role', `Role "${roleName}" is not available at this campus.${options}`);
      } else if (typeof canAssignWeight === 'function'
        && !canAssignWeight(role.user_role_weight, myRoleWeight)) {
        /* Same authority rule the drawer's role dropdown applies. Without it the
           upload would be a way to create a role the uploader cannot select in the
           UI, which is a privilege escalation rather than a convenience. */
        addIssue(rowNum, 'role', `Your role is not permitted to create "${roleName}" users.`);
      }
    }

    // Email: required unless caregiver; format-checked whenever a value is present.
    if (!email) {
      if (requiresEmail(roleName)) {
        addIssue(rowNum, 'email', `Missing email — required for role "${roleName || 'unknown'}".`);
      }
    } else if (!EMAIL_PATTERN.test(email)) {
      addIssue(rowNum, 'email', `"${email}" is not a valid email address.`);
    }

    if (!password) {
      addIssue(rowNum, 'password', 'Missing password.');
    } else if (password.length < PASSWORD_MIN_LENGTH) {
      addIssue(rowNum, 'password', `Password must be at least ${PASSWORD_MIN_LENGTH} characters.`);
    } else if (!PASSWORD_PATTERN.test(password)) {
      addIssue(rowNum, 'password', 'Password must be letters and numbers only, with no spaces or symbols.');
    } else if (role && ADMIN_ROLE_IDS.includes(parseInt(role.user_role_id, 10))
      && password === WEAK_ADMIN_PASSWORD) {
      /* Checked here rather than left to the backend so the preflight's verdict
         means what it says: the drawer refuses this combination on add, and a row
         it would refuse must not pass as good. */
      addIssue(rowNum, 'password', `Admin roles cannot use "${WEAK_ADMIN_PASSWORD}" as a password.`);
    }

    if (role) {
      payloads.push({
        rowNum,
        display_name: displayName,
        username,
        email,
        user_role_id: role.user_role_id,
        password,
        /* Not in the file, and sent explicitly rather than left undefined so the
           payload this builds matches what the drawer sends for an add. */
        group_ids: [],
        phone: '',
        title: '',
        reports: [],
      });
    }
  });

  const ok = issues.length === 0;
  return {
    ok,
    totalRows: dataCount,
    issues,
    /* All-or-nothing: a partly-valid file uploads nothing. Half-applying a user
       import leaves someone reconciling which rows landed. */
    payloads: ok ? payloads : [],
  };
}

/**
 * Split payloads into request-sized groups.
 *
 * `size` of 0 (or anything non-positive) returns a single group holding
 * everything — one request. See UPLOAD_CHUNK_SIZE, which is 10 today.
 */
export function chunkPayloads(payloads, size = UPLOAD_CHUNK_SIZE) {
  const list = payloads || [];
  if (!list.length) return [];
  if (!size || size <= 0) return [list];
  const out = [];
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
  return out;
}

/**
 * Submit the payloads, one request per chunk, stopping at the first failure.
 *
 * Stopping rather than pressing on: if the first request failed there is usually
 * something wrong with the whole batch, and continuing turns one clear failure
 * into a partial import nobody can reconcile. `sent` reports how many rows did
 * land, so the report can say exactly where it stopped.
 *
 * @param {Array<object>} payloads
 * @param {object} options
 * @param {(chunk: Array<object>) => Promise<[boolean, string?]>} options.submit
 * @param {number} [options.chunkSize]
 * @param {(done: number, total: number, requests: number) => void} [options.onProgress]
 *        `requests` is how many requests the upload was split into. Emitted once
 *        up front and again after each one, so a caller can tell "no progress yet"
 *        from "there will never be intermediate progress" — with a single request
 *        an X-of-Y counter sits on 0 for the whole wait and reads as broken.
 * @returns {Promise<{ok: boolean, sent: number, total: number, error: string}>}
 */
export async function uploadInChunks(payloads, { submit, chunkSize = UPLOAD_CHUNK_SIZE, onProgress } = {}) {
  const groups = chunkPayloads(payloads, chunkSize);
  const total = (payloads || []).length;
  let sent = 0;

  /* Announce the shape before any work starts, so the UI can choose its wording
     before the first request rather than after the last one. */
  onProgress?.(0, total, groups.length);

  for (let i = 0; i < groups.length; i += 1) {
    /* Sequential on purpose. These are creates against one campus; firing the
       chunks in parallel would race on username uniqueness and make a failure
       impossible to attribute to a row. */
    // eslint-disable-next-line no-await-in-loop
    const [ok, err] = await submit(groups[i]);
    if (!ok) {
      return { ok: false, sent, total, error: err || 'Upload failed.' };
    }
    sent += groups[i].length;
    onProgress?.(sent, total, groups.length);
  }

  return { ok: true, sent, total, error: '' };
}
